import { Locale, Locales } from "@/types/locale";
import { InvariantValue } from "@/types/squidex";
import { ContributorOutputView } from "@features/contributors/types/contributor";
import {
  ContributorBiographyInlineOutputView,
  ContributorEventProfileInlineInputView,
} from "@features/proposals/types/member";

const localizeContributorValue = (value?: string) => ({
  ru: value,
  en: value,
});

const twitterContactComponentMetadata = {
  schemaId: "9e088ec6-9545-4b2e-a4a5-1f301304aa00",
  schemaName: "srm-biography-contact",
} as const;

const createTwitterContact = (value: string): InvariantValue => ({
  ...twitterContactComponentMetadata,
  type: "twitter",
  value,
});

export const buildContributorEventProfileDefaults = (
  contributor?: ContributorOutputView,
): ContributorEventProfileInlineInputView => {
  const profile = contributor?.profile;
  const twitter = profile?.contacts?.twitter?.value;

  return {
    biography: {
      content: {
        data: {
          firstName: localizeContributorValue(profile?.name?.firstName),
          lastName: localizeContributorValue(profile?.name?.lastName),
          alias: localizeContributorValue(profile?.name?.alias),
          company: localizeContributorValue(profile?.company?.legalName),
          position: localizeContributorValue(profile?.position?.title),
          ...(twitter ? { contacts: { iv: [createTwitterContact(twitter)] } } : {}),
        },
      },
    },
  };
};

export const replaceTwitterContact = (
  contacts: readonly InvariantValue[] | undefined,
  value: string,
): InvariantValue[] => {
  const existingContacts = contacts ?? [];

  if (!value) {
    return existingContacts.filter((contact) => contact.type !== "twitter");
  }

  const twitterIndex = existingContacts.findIndex((contact) => contact.type === "twitter");

  if (twitterIndex === -1) {
    return [...existingContacts, createTwitterContact(value)];
  }

  return existingContacts.flatMap((contact, index) => {
    if (contact.type !== "twitter") {
      return [contact];
    }

    if (index !== twitterIndex) {
      return [];
    }

    return [{ ...contact, ...twitterContactComponentMetadata, type: "twitter", value }];
  });
};

export interface MemberBiographyInputView {
  readonly eventProfile: ContributorEventProfileInlineInputView;
}

export const buildBiographyReuseInput = (
  biography: ContributorBiographyInlineOutputView,
): MemberBiographyInputView => ({
  eventProfile: {
    biography: {
      content: {
        data: biography.content?.data,
      },
    },
  },
});

export const resolveBiographyLocales = (
  biography?: ContributorBiographyInlineOutputView,
): Locale[] => {
  void biography;

  return [...Locales];
};
